import React from 'react';
import { motion } from 'framer-motion';

interface BooleanControlProps {
    value: boolean;
    onChange: (newValue: boolean) => void;
    label: string;
    id: string;
}

export const BooleanControl = ({ value, onChange, label, id: _id }: BooleanControlProps) => {
    return (
        <div style={styles.container}>
            <motion.div
                style={{
                    ...styles.button,
                    ...(value ? styles.buttonSelected : {}),
                }}
                whileHover={{ 
                    scale: 1.02,
                    y: -1,
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onChange(!value)}
                title={`${label}: ${value ? 'On' : 'Off'}`}
            >
                {/* Cosmic glow effect for selected */}
                {value && (
                    <motion.div
                        style={styles.selectedGlow}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    />
                )}
                
                {/* Checkbox content */}
                <div style={styles.content}>
                    {/* Checkbox icon container */}
                    <div style={{
                        ...styles.iconContainer,
                        color: value ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.5)',
                    }}>
                        {/* Custom cosmic checkbox icon */}
                        <motion.div
                            style={styles.checkboxIcon}
                            animate={{
                                backgroundColor: value ? 
                                    'rgba(147, 112, 219, 0.8)' :
                                    'rgba(255, 255, 255, 0.1)',
                                borderColor: value ?
                                    'rgba(147, 112, 219, 0.9)' :
                                    'rgba(255, 255, 255, 0.3)',
                            }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* Checkmark */}
                            <motion.svg
                                style={styles.checkmark}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ 
                                    pathLength: value ? 1 : 0,
                                    opacity: value ? 1 : 0,
                                    scale: value ? 1 : 0.8,
                                }}
                                transition={{ 
                                    duration: 0.3,
                                    ease: "easeOut",
                                }}
                            >
                                <motion.path 
                                    d="m9 12 2 2 4-4"
                                    style={{ 
                                        filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8))',
                                        color: 'white',
                                    }}
                                />
                            </motion.svg>
                        </motion.div>
                    </div>
                    
                    {/* Label */}
                    <span style={{
                        ...styles.label,
                        color: value ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)',
                        textShadow: value ? '0 0 10px rgba(147, 112, 219, 0.6)' : 'none',
                    }}>
                        {label}
                    </span>
                </div>

                {/* Status indicator dot */}
                <motion.div
                    style={{
                        ...styles.statusDot,
                        backgroundColor: value ? 
                            'rgba(147, 112, 219, 1)' : 
                            'rgba(255, 255, 255, 0.3)',
                    }}
                    animate={{
                        scale: value ? [1, 1.2, 1] : 1,
                        boxShadow: value ? 
                            '0 0 15px rgba(147, 112, 219, 0.8), 0 0 30px rgba(147, 112, 219, 0.4)' :
                            '0 0 5px rgba(255, 255, 255, 0.2)',
                    }}
                    transition={{ 
                        scale: { duration: 0.3 },
                        backgroundColor: { duration: 0.2 },
                        boxShadow: { duration: 0.3 },
                    }}
            />
            </motion.div>
        </div>
    );
};

// --- Cosmic Style Definitions ---
const styles: { [key: string]: React.CSSProperties } = {
    container: {
        marginBottom: '12px',
    },
    button: {
        position: 'relative',
        background: `
            linear-gradient(135deg, 
                rgba(255, 255, 255, 0.06) 0%, 
                rgba(255, 255, 255, 0.02) 100%
            )
        `,
        backdropFilter: 'blur(15px) saturate(180%)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        borderRadius: '14px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: `
            0 4px 16px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            0 1px 0 rgba(255, 255, 255, 0.05)
        `,
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 'fit-content',
        maxWidth: '280px',
    },
    buttonSelected: {
        background: `
            linear-gradient(135deg, 
                rgba(147, 112, 219, 0.2) 0%, 
                rgba(138, 43, 226, 0.12) 50%,
                rgba(75, 0, 130, 0.15) 100%
            )
        `,
        borderColor: 'rgba(147, 112, 219, 0.4)',
        borderWidth: '2px',
        boxShadow: `
            0 0 25px rgba(147, 112, 219, 0.3),
            0 0 50px rgba(147, 112, 219, 0.15),
            0 8px 32px rgba(0, 0, 0, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.15)
        `,
    },
    selectedGlow: {
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        right: '-50%',
        bottom: '-50%',
        background: `
            radial-gradient(circle at center, 
                rgba(147, 112, 219, 0.2) 0%, 
                rgba(147, 112, 219, 0.08) 40%, 
                transparent 70%
            )
        `,
        borderRadius: '50%',
        zIndex: 0,
        pointerEvents: 'none',
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
    },
    iconContainer: {
        marginRight: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        flexShrink: 0,
    },
    checkboxIcon: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        backdropFilter: 'blur(5px)',
    },
    checkmark: {
        width: '14px',
        height: '14px',
    },
    label: {
        fontSize: '13px',
        fontWeight: '500',
        letterSpacing: '0.3px',
        transition: 'all 0.3s ease',
        fontFamily: "'Inter', system-ui, sans-serif",
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    statusDot: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        zIndex: 1,
    }
}; 